import React, { useCallback, useRef, useState } from 'react';
import {
  CafExampleConflict, CafExampleConflictRequest, CafFinalResponseStance, checkExampleConflicts,
} from '../../../api/cafCorpus';
import { actionCopy, finalResponseWords } from '../copy';
import { useFinalResponseSupported } from '../finalResponseSupport';

export interface ConflictProposal {
  action: string;
  final_response_stance: string;
  workflowName?: string | null;
}

/** Ruling R7: similar same-sender examples that say something different, beside this one. Warn only. */
export const ConflictWarnings: React.FC<{ conflicts: CafExampleConflict[]; proposal: ConflictProposal }> = ({ conflicts, proposal }) => {
  if (!conflicts.length) return null;
  const changed = (item: CafExampleConflict, reason: CafExampleConflict['reasons'][number]) =>
    (item.reasons.includes(reason) ? 'cur-chg-now' : undefined);
  return (
    <div className="cur-alert cur-alert--warn" role="alert">
      <div className="cur-grow">
        <p><b>Similar examples from this sender say something different.</b> You can still save. Check them first.</p>
        <table className="cur-ba" aria-label="This example and similar examples">
          <thead><tr><th>Example</th><th>Final response?</th><th>What should happen</th><th>Workflow</th></tr></thead>
          <tbody>
            <tr>
              <td>This example</td><td>{finalResponseWords(proposal.final_response_stance)}</td>
              <td>{actionCopy(proposal.action).label}</td><td>{proposal.workflowName ?? '—'}</td>
            </tr>
            {conflicts.map((item) => (
              <tr key={item.entry_id}>
                <td>{item.subject || 'No subject'}</td>
                <td className={changed(item, 'opposite_setting')}>{finalResponseWords(item.final_response_stance)}</td>
                <td className={changed(item, 'different_action')}>{actionCopy(item.correct_action).label}</td>
                <td className={changed(item, 'different_workflow')}>{item.workflow_name ?? 'None'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/** A save's check request; the setting is absent on a backend without it (LabelWrite). */
export type ConflictCheckRequest = Omit<CafExampleConflictRequest, 'final_response_stance'> & {
  final_response_stance?: CafFinalResponseStance;
};

export interface ConflictGate {
  conflicts: CafExampleConflict[];
  /** Warnings on screen. Saves must use the `warnings` returned by confirmOrWarn, not this. */
  warnings: string[];
  /** `warnings` is the list the user saw for exactly this request; empty otherwise. */
  confirmOrWarn: (request: ConflictCheckRequest) => Promise<{ proceed: boolean; warnings: string[] }>;
}

/** First click checks; with conflicts it shows them and stops. The same save clicked again goes
 *  through ("Save anyway"); a changed form checks again. A check that fails never blocks (R7).
 *  A click while a check is running is ignored, so a double click saves once. A backend without
 *  the final-response setting has no check, so none is asked for (final fix wave C2). */
export const useConflictGate = (): ConflictGate => {
  const [shown, setShownState] = useState<{ key: string; conflicts: CafExampleConflict[]; warnings: string[] } | null>(null);
  const shownRef = useRef<typeof shown>(null);
  const checking = useRef(false);
  const supported = useRef(true);
  supported.current = useFinalResponseSupported();
  const setShown = useCallback((value: typeof shown) => { shownRef.current = value; setShownState(value); }, []);
  const confirmOrWarn = useCallback(async (proposed: ConflictCheckRequest) => {
    if (checking.current) return { proceed: false, warnings: [] as string[] };
    const stance = proposed.final_response_stance;
    if (!supported.current || !stance) {
      setShown(null);
      return { proceed: true, warnings: [] as string[] };
    }
    const request: CafExampleConflictRequest = { ...proposed, final_response_stance: stance };
    const key = JSON.stringify(request);
    const seen = shownRef.current;
    if (seen && seen.key === key) return { proceed: true, warnings: seen.warnings };
    checking.current = true;
    try {
      const result = await checkExampleConflicts(request);
      if (!result.conflicts.length) {
        setShown(null);
        return { proceed: true, warnings: [] as string[] };
      }
      setShown({ key, conflicts: result.conflicts, warnings: result.warnings });
      return { proceed: false, warnings: [] as string[] };
    } catch {
      setShown(null);
      return { proceed: true, warnings: [] as string[] };
    } finally {
      checking.current = false;
    }
  }, [setShown]);
  return { conflicts: shown?.conflicts ?? [], warnings: shown?.warnings ?? [], confirmOrWarn };
};
